'use client'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronRight, CheckCircle2, XCircle } from 'lucide-react'
import { StarIcon } from './medallions/star-icon'
import { usePlayLibrary } from '@/components/phishtrainer/hooks/use-play-library'
import { RoundDetailSheet } from '@/app/play/library/_components/round-detail-sheet'
import type { EnrichedRound } from '@/app/api/phishtrainer/users/[id]/library/route'
import { useTranslation } from '@/lib/i18n'

export interface RecentRound {
  id: string
  outcome: 'passed' | 'failed' | 'partial'
  pointsEarned: number
  stars: number
  timeAgo: string
  attack: string
  pillar: string
  /** All pillars touched by the round. When provided, every pillar renders as
   * its own badge; otherwise the single `pillar` prop is used (backward compat
   * with /play which still passes one pillar). */
  pillars?: string[]
}

export interface RecentRoundsCardProps {
  rounds: RecentRound[]
  historyHref?: string
  /** When provided, each round row becomes a link to its full detail (the
   *  performance drawer). Omitted on surfaces with no detail view (admin).
   *  DEPRECATED for the play surfaces in favour of `userId` — a plain href
   *  teleported the learner to the Library page to see a drawer that should
   *  just open where they clicked. Kept for any caller that still wants a
   *  hard navigation. */
  roundHref?: (roundId: string) => string
  /** When set, clicking a round opens its full-detail drawer IN PLACE — an
   *  overlay on the CURRENT page — instead of navigating away. The enriched
   *  round is loaded once (lazily prefetched) from the learner's library, which
   *  resolves in BOTH the play realm (own history) and the admin realm (viewing
   *  a learner). This is the fix for "clicking a round jumps me to another page
   *  and opens the drawer there" — the drawer now opens right here. */
  userId?: string
}

export function RecentRoundsCard({ rounds, historyHref = '#', roundHref, userId }: RecentRoundsCardProps) {
  const { t } = useTranslation()
  const inPlace = !!userId
  const [selected, setSelected] = useState<EnrichedRound | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Reads the SAME cache entry the /play/library page populates — this card only
  // needs `.rounds` off that payload, and used to re-fetch the whole thing for
  // itself. The drawer still opens instantly on first click; it is now a cache
  // read rather than a duplicate transfer.
  const { data: libraryData } = usePlayLibrary<{ rounds?: EnrichedRound[] }>(userId)
  const enriched: EnrichedRound[] | null = libraryData ? (libraryData.rounds ?? []) : null

  const openInPlace = (roundId: string) => {
    const match = (enriched ?? []).find((e) => e.round?.id === roundId)
    if (!match) return
    setSelected(match)
    setSheetOpen(true)
  }

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle>{t('learner.cards.recentRounds.title')}</CardTitle>
        {/* Only render "All history" when it points somewhere real — the default
            '#' is a dead link (e.g. the admin user page, which has no play
            history route to send an operator to). */}
        {historyHref && historyHref !== '#' && (
          <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
            <Link href={historyHref}>{t('learner.cards.recentRounds.allHistory')} <ChevronRight className="h-4 w-4 rtl-flip" /></Link>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {rounds.map((r) => {
            const rowInner = (
            <div
              className={
                'flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 transition-colors ' +
                (roundHref || inPlace ? 'hover:bg-muted hover:border-primary/30 cursor-pointer' : 'hover:bg-muted')
              }
            >
              <div
                className={
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ' +
                  (r.outcome === 'passed' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/15 text-red-600 dark:text-red-400')
                }
              >
                {r.outcome === 'passed' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-foreground">{r.attack}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                  <span>{r.timeAgo}</span>
                  {(r.pillars && r.pillars.length > 0 ? r.pillars : r.pillar ? [r.pillar] : []).map((p) => (
                    <Badge key={p} variant="outline" className="text-[9px] py-0 px-1.5">{p}</Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.stars > 0 && (
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: r.stars }).map((_, i) => (
                      <StarIcon key={i} size={12} />
                    ))}
                  </div>
                )}
                <span className="text-xs font-semibold tabular-nums text-muted-foreground min-w-[50px] text-end">{t('learner.cards.recentRounds.points', { points: r.pointsEarned })}</span>
              </div>
            </div>
            )
            // In-place drawer (preferred): a button that opens the detail sheet
            // right here, disabled only until the one-time prefetch lands.
            if (inPlace) {
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => openInPlace(r.id)}
                  disabled={enriched === null}
                  aria-label={t('learner.cards.recentRounds.viewRound', { attack: r.attack })}
                  className="block w-full text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg disabled:cursor-progress"
                >
                  {rowInner}
                </button>
              )
            }
            return roundHref ? (
              <Link
                key={r.id}
                href={roundHref(r.id)}
                aria-label={t('learner.cards.recentRounds.viewRound', { attack: r.attack })}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
              >
                {rowInner}
              </Link>
            ) : (
              <div key={r.id}>{rowInner}</div>
            )
          })}
        </div>
      </CardContent>
      {inPlace && (
        <RoundDetailSheet item={selected} open={sheetOpen} onOpenChange={setSheetOpen} />
      )}
    </Card>
  )
}
